import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Client} from "@stomp/stompjs";
import SockJS from "sockjs-client/dist/sockjs.min.js";
import {AuthApi, Api_url} from "../../network/api/NETWORKCONSTS";
import {WebSocketContext} from "./WebSocketContext";
import {applyConfig, setCoBrowseSender, startRecording, stopRecording} from "./coBrowseManager";

if (typeof window !== "undefined" && window.global === undefined) {
    window.global = window;
}

export const APP_NAME = "WEBAPP";

function chatEndpoint() {
    return AuthApi.BASECHATURL + Api_url.Chat.endpoint;
}

function parseBody(frame) {
    try {
        return JSON.parse(frame?.body || "{}");
    } catch (_e) {
        return {};
    }
}

export function WebSocketProvider({children}) {
    const token = useSelector((state) => state.auth.token);
    const myUserId = useSelector((state) => state.auth.user?.Id);
    const tokenRef = useRef(token);
    const userIdRef = useRef(myUserId);
    const clientRef = useRef(null);
    const listenersRef = useRef(new Set());
    const reconnectTimerRef = useRef(null);
    const hasConnectedOnceRef = useRef(false);
    // ActivationState in @stomp/stompjs is numeric (ACTIVE=0); UI needs real STOMP link.
    const [connected, setConnected] = useState(false);
    const [status, setStatus] = useState("INACTIVE");

    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    useEffect(() => {
        userIdRef.current = myUserId;
    }, [myUserId]);

    const clearReconnectTimer = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, []);

    const syncConnectionState = useCallback((client, isLinked) => {
        setConnected(!!isLinked);
        if (isLinked) {
            setStatus("CONNECTED");
        } else if (client?.active) {
            setStatus("CONNECTING");
        } else {
            setStatus("INACTIVE");
        }
    }, []);

    const deactivateClient = useCallback(async () => {
        stopRecording();
        const client = clientRef.current;
        clientRef.current = null;
        if (client) {
            try {
                await client.deactivate();
            } catch (_e) {
                // ignore
            }
        }
        setConnected(false);
        setStatus("INACTIVE");
    }, []);

    const publishEvents = useCallback((events, approxBytes) => {
        const client = clientRef.current;
        const uid = userIdRef.current;
        // Never publish before START / while disconnected / empty batch.
        if (!client?.connected || !uid || !events?.length) return false;
        if (events.length > 500) return false;
        const destination = `/app/cobrowse.events/${uid}`;
        try {
            client.publish({
                destination,
                body: JSON.stringify({Events: events})
            });
            console.log("[CoBrowse][user] events-send", {destination, count: events.length, approxBytes});
            return true;
        } catch (_e) {
            return false;
        }
    }, []);

    const handleControl = useCallback((payload) => {
        const action = payload?.Action;
        console.log("[CoBrowse][user] control-received", payload);
        if (action === "START") {
            startRecording();
        } else if (action === "STOP") {
            stopRecording();
        } else if (action === "CONFIG") {
            applyConfig(payload?.Settings);
        }
    }, []);

    const activateClient = useCallback(() => {
        const currentToken = tokenRef.current;
        const uid = userIdRef.current;
        if (!currentToken || !uid) return;

        const existing = clientRef.current;
        if (existing) {
            if (existing.active || existing.connected) return;
            try {
                existing.deactivate();
            } catch (_e) {
                // ignore
            }
            clientRef.current = null;
        }

        const endpoint = chatEndpoint();
        const client = new Client({
            // Prefer native WS; polling/xhr-streaming makes CoBrowse unusable.
            webSocketFactory: () => new SockJS(endpoint, null, {transports: ["websocket"]}),
            reconnectDelay: 3000,
            heartbeatIncoming: 0,
            heartbeatOutgoing: 0,
            connectHeaders: {
                Authorization: "Bearer " + currentToken,
                AppName: APP_NAME
            },
            beforeConnect: () => {
                client.connectHeaders = {
                    Authorization: "Bearer " + (tokenRef.current || ""),
                    AppName: APP_NAME
                };
            },
            onConnect: () => {
                clearReconnectTimer();
                const userId = userIdRef.current;
                stopRecording();
                syncConnectionState(client, true);
                if (!userId || !client.connected) return;
                const supportDest = `/chat/support/${userId}`;
                const controlDest = `/chat/cobrowse/control/${userId}`;
                client.subscribe(supportDest, (frame) => {
                    const msg = parseBody(frame);
                    listenersRef.current.forEach((listener) => {
                        try {
                            listener(msg);
                        } catch (_e) {
                            // ignore listener errors
                        }
                    });
                });
                client.subscribe(controlDest, (frame) => {
                    handleControl(parseBody(frame));
                });
                console.log("[CoBrowse][user] control-subscribed", {destination: controlDest});
                if (hasConnectedOnceRef.current) {
                    console.log("[CoBrowse][user] re-subscribed-after-reconnect", {
                        myUserId: userId,
                        destination: controlDest
                    });
                }
                hasConnectedOnceRef.current = true;
            },
            onDisconnect: () => {
                stopRecording();
                syncConnectionState(client, false);
            },
            onStompError: () => {
                stopRecording();
                syncConnectionState(client, false);
            },
            onWebSocketClose: () => {
                stopRecording();
                syncConnectionState(client, false);
                clearReconnectTimer();
                // STOMP reconnectDelay covers most cases; short fallback if still dead.
                reconnectTimerRef.current = setTimeout(() => {
                    if (!tokenRef.current || !userIdRef.current) return;
                    if (clientRef.current?.connected || clientRef.current?.active) return;
                    try {
                        syncConnectionState(client, false);
                        client.activate();
                    } catch (_e) {
                        // ignore
                    }
                }, 2500);
            },
            onChangeState: () => {
                syncConnectionState(client, !!client.connected);
            }
        });

        setCoBrowseSender(publishEvents);
        clientRef.current = client;
        syncConnectionState(client, false);
        client.activate();
    }, [clearReconnectTimer, handleControl, publishEvents, syncConnectionState]);

    const reactive = useCallback(() => {
        if (!tokenRef.current || !userIdRef.current) return;
        const client = clientRef.current;
        if (client?.connected || client?.active) return;
        activateClient();
    }, [activateClient]);

    useEffect(() => {
        if (token && myUserId) {
            activateClient();
        } else {
            deactivateClient();
        }
        return () => {
            clearReconnectTimer();
            deactivateClient();
        };
    }, [token, myUserId, activateClient, deactivateClient, clearReconnectTimer]);

    useEffect(() => {
        const tryReconnect = () => {
            if (document.visibilityState && document.visibilityState !== "visible") return;
            reactive();
        };
        window.addEventListener("online", tryReconnect);
        window.addEventListener("focus", tryReconnect);
        window.addEventListener("pageshow", tryReconnect);
        document.addEventListener("visibilitychange", tryReconnect);
        const stopOnLeave = () => stopRecording();
        window.addEventListener("pagehide", stopOnLeave);
        window.addEventListener("beforeunload", stopOnLeave);
        return () => {
            window.removeEventListener("online", tryReconnect);
            window.removeEventListener("focus", tryReconnect);
            window.removeEventListener("pageshow", tryReconnect);
            document.removeEventListener("visibilitychange", tryReconnect);
            window.removeEventListener("pagehide", stopOnLeave);
            window.removeEventListener("beforeunload", stopOnLeave);
        };
    }, [reactive]);

    const sendChatMessage = useCallback((msg, onStatus) => {
        const client = clientRef.current;
        const uid = userIdRef.current;
        if (!client?.connected || !uid) {
            onStatus?.(false);
            return;
        }
        try {
            client.publish({
                destination: `/app/support.chat/${uid}`,
                body: JSON.stringify(msg)
            });
            onStatus?.(true);
        } catch (_e) {
            onStatus?.(false);
        }
    }, []);

    const addChatListener = useCallback((listener) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);

    const value = useMemo(() => ({
        connected,
        status,
        reactive,
        sendChatMessage,
        addChatListener,
        myUserId
    }), [connected, status, reactive, sendChatMessage, addChatListener, myUserId]);

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
}
